import { useState, useMemo, useEffect } from 'react';
import { ClubEvent, Attendance, UserProfile } from '../types';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { cleanIcsString, safeFormatISO } from '../lib/utils';
import { collection, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Calendar as CalendarIcon, MapPin, User, Clock, CheckCircle2, XCircle, Info, List, Clock as ClockIcon, ChevronRight, Search, Filter, SortAsc, SortDesc } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { EVENT_TYPES } from '../constants';
import EventChat from './EventChat';

interface EventListProps {
  events: ClubEvent[];
  attendance: Attendance[];
  currentUser: UserProfile;
}

export default function EventList({ events, attendance, currentUser }: EventListProps) {
  const [timeFilter, setTimeFilter] = useState<'all' | 'upcoming' | 'past'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date-desc' | 'date-asc' | 'name-asc' | 'name-desc'>('date-desc');
  const [selectedEventForDetails, setSelectedEventForDetails] = useState<ClubEvent | null>(null);

  const filteredEvents = useMemo(() => {
    if (!currentUser) return [];
    return events.filter(event => {
      // Time filter
      const isPast = new Date(event.startDate) < new Date();
      if (timeFilter === 'upcoming' && isPast) return false;
      if (timeFilter === 'past' && !isPast) return false;

      // Search filter (name)
      if (searchQuery && !event.name.toLowerCase().includes(searchQuery.toLowerCase())) return false;

      // Type filter
      if (typeFilter !== 'all' && event.type !== typeFilter) return false;

      return true;
    }).sort((a, b) => {
      // Primary sort by Mission Date (Stardate)
      const dateA = new Date(a.startDate).getTime();
      const dateB = new Date(b.startDate).getTime();
      
      if (sortBy === 'date-desc') return dateB - dateA;
      if (sortBy === 'date-asc') return dateA - dateB;
      if (sortBy === 'name-asc') return a.name.localeCompare(b.name);
      if (sortBy === 'name-desc') return b.name.localeCompare(a.name);
      return 0;
    });
  }, [events, timeFilter, searchQuery, typeFilter, sortBy, currentUser]);

  // Sync sorting with filter changes - disabled to respect "current to latest" request
  /*
  useEffect(() => {
    if (timeFilter === 'upcoming') setSortBy('date-asc');
    else if (timeFilter === 'past') setSortBy('date-desc');
  }, [timeFilter]);
  */

  const isPlannerOrAdmin = currentUser?.roles?.some(r => 
    ['admin', 'user', 'con-planner', 'dept_lead', 'user_manager'].includes(r)
  );

  const handleRSVP = async (eventId: string, status: 'attending' | 'not-attending') => {
    const existing = attendance.find(a => a.eventId === eventId && a.userId === currentUser.uid);
    const assignedStatus = status === 'attending' 
      ? (isPlannerOrAdmin ? 'attending' : 'pending')
      : 'not-attending';
    
    try {
      if (existing) {
        await updateDoc(doc(db, 'attendance', existing.id!), {
          status: assignedStatus,
          timestamp: new Date().toISOString(),
          ...(isPlannerOrAdmin && status === 'attending' ? { confirmedBy: currentUser.uid, confirmedAt: new Date().toISOString() } : {})
        });
      } else {
        await addDoc(collection(db, 'attendance'), {
          userId: currentUser.uid,
          eventId,
          status: assignedStatus,
          timestamp: new Date().toISOString(),
          ...(isPlannerOrAdmin && status === 'attending' ? { confirmedBy: currentUser.uid, confirmedAt: new Date().toISOString() } : {})
        });
      }

      if (status === 'not-attending') {
        toast.info('Attendance cancelled.');
      } else if (isPlannerOrAdmin) {
        toast.success('Attendance confirmed (+points awarded)!');
      } else {
        toast.success('Attendance submitted as PENDING. An Admin or Con Planner will verify to award your points.');
      }
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, 'attendance');
    }
  };

  if (!currentUser) return null;

  const latestMonthlyReport = events
    .filter(e => e.type === 'Monthly Report')
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())[0];

  return (
    <div className="space-y-8">
      {latestMonthlyReport && (
        <div className="bg-lcars-gold/10 border-l-4 lg:border-l-8 border-lcars-gold p-4 lg:p-6 rounded-r-full">
          <div className="flex items-center gap-4 mb-4">
            <div className="p-2 bg-lcars-gold text-black rounded-full shrink-0">
              <CalendarIcon className="w-5 h-5 lg:w-6 lg:h-6" />
            </div>
            <div>
              <h2 className="text-xl lg:text-2xl font-bold text-lcars-gold uppercase tracking-tight">Latest Monthly Report</h2>
              <p className="text-[0.6rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">STARDATE: {safeFormatISO(latestMonthlyReport.startDate, 'yyyy.MM.dd', 'TBD')}</p>
            </div>
          </div>
          <div className="space-y-4">
            <h3 className="text-lg lg:text-xl font-bold text-[var(--foreground)]">{cleanIcsString(latestMonthlyReport.name)}</h3>
            <p className="text-[var(--foreground)]/80 text-sm lg:text-base leading-relaxed italic">
              {cleanIcsString(latestMonthlyReport.location) || 'No summary provided for this period.'}
            </p>
            <div className="flex flex-wrap gap-2 lg:gap-4">
              <div className="bg-lcars-gold/20 px-3 lg:px-4 py-1 rounded-full text-lcars-gold font-bold text-[0.65rem] lg:text-xs">
                HOST: {latestMonthlyReport.host || 'COMMAND'}
              </div>
              <div className="bg-lcars-gold/20 px-3 lg:px-4 py-1 rounded-full text-lcars-gold font-bold text-[0.65rem] lg:text-xs">
                POINTS: {latestMonthlyReport.points}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
          <div className="flex items-center gap-4">
            <div className="h-12 w-4 bg-lcars-blue rounded-full shrink-0"></div>
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tighter text-lcars-blue whitespace-nowrap uppercase">
                Memory Alpha
              </h2>
              <p className="text-[0.6rem] lg:text-[0.7rem] text-[var(--foreground)]/40 font-bold tracking-widest uppercase">
                Ship's historical archives and operational logs.
              </p>
            </div>
          </div>
          <div className="flex gap-1.5 bg-lcars-tan/10 p-1.5 rounded-full border-2 border-lcars-tan w-full lg:w-auto overflow-x-auto">
            <button 
              className={`lcars-pill ${timeFilter === 'upcoming' ? 'bg-lcars-blue text-black' : 'text-lcars-tan hover:bg-lcars-tan/20'}`}
              onClick={() => setTimeFilter('upcoming')}
            >
              UPCOMING
            </button>
            <button 
              className={`lcars-pill ${timeFilter === 'past' ? 'bg-lcars-blue text-black' : 'text-lcars-tan hover:bg-lcars-tan/20'}`}
              onClick={() => setTimeFilter('past')}
            >
              PAST
            </button>
            <button 
              className={`lcars-pill ${timeFilter === 'all' ? 'bg-lcars-blue text-black' : 'text-lcars-tan hover:bg-lcars-tan/20'}`}
              onClick={() => setTimeFilter('all')}
            >
              ALL
            </button>
          </div>
        </div>

        {/* Search and Advanced Filters */}
        <div className="flex flex-col md:flex-row gap-4 bg-white/5 p-4 rounded-r-[40px] border-l-4 border-lcars-blue">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-lcars-blue" />
            <Input 
              placeholder="SEARCH MISSION DATABASE..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[var(--background)] border-lcars-blue/30 focus:border-lcars-blue text-[var(--foreground)] h-10 uppercase text-[0.7rem] tracking-widest"
            />
          </div>
          
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-lcars-tan" />
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-40 bg-[var(--background)] border-lcars-tan/30 text-[var(--foreground)] h-10 text-[0.7rem] uppercase tracking-widest">
                  <SelectValue placeholder="MISSION TYPE" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--background)] border-lcars-tan text-[var(--foreground)]">
                  <SelectItem value="all">ALL TYPES</SelectItem>
                  {EVENT_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type.toUpperCase()}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <div className="p-2 bg-lcars-gold/10 rounded">
                <SortAsc className="w-4 h-4 text-lcars-gold" />
              </div>
              <Select value={sortBy} onValueChange={(val: any) => setSortBy(val)}>
                <SelectTrigger className="w-48 bg-[var(--background)] border-lcars-gold/30 text-[var(--foreground)] h-10 text-[0.7rem] uppercase tracking-widest">
                  <SelectValue placeholder="SORT BY" />
                </SelectTrigger>
                <SelectContent className="bg-[var(--background)] border-lcars-gold text-[var(--foreground)]">
                  <SelectItem value="date-desc">LATEST STARDATE (DEC → JAN)</SelectItem>
                  <SelectItem value="date-asc">EARLIEST STARDATE (JAN → DEC)</SelectItem>
                  <SelectItem value="name-asc">MISSION NAME (A-Z)</SelectItem>
                  <SelectItem value="name-desc">MISSION NAME (Z-A)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 lg:gap-8">
        {filteredEvents.map(event => {
          const userRSVP = attendance.find(a => a.eventId === event.id && a.userId === currentUser.uid);
          const isAttending = userRSVP?.status === 'attending';
          const isPast = new Date(event.startDate) < new Date();

          return (
            <div key={event.id} className="bg-[var(--background)] border-l-8 border-lcars-blue flex flex-col hover:bg-lcars-blue/5 transition-all group">
              <div className="bg-lcars-blue text-black px-4 py-1 flex justify-between items-center font-bold text-[0.7rem] tracking-widest">
                <span>{event.type.toUpperCase()}</span>
                <span className="bg-[var(--background)] text-lcars-blue px-2 py-0.5 rounded-full">
                  {event.points} PTS
                </span>
              </div>
              
              <div className="p-6 flex-grow space-y-4">
                <h3 className="text-xl font-bold group-hover:text-lcars-blue transition-colors leading-tight">{cleanIcsString(event.name)}</h3>
                
                <div className="space-y-3 text-[0.85rem] font-bold">
                  <div className="flex items-center gap-3 text-lcars-purple">
                    <CalendarIcon className="w-4 h-4" />
                    <span className="tracking-widest">{safeFormatISO(event.startDate, 'yyyy.MM.dd', 'TBD')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-lcars-purple">
                    <Clock className="w-4 h-4" />
                    <span className="tracking-widest">
                      {safeFormatISO(event.startDate, 'HH:mm', 'TBD')} {event.endDate ? `- ${safeFormatISO(event.endDate, 'HH:mm')}` : ''}
                    </span>
                  </div>
                  {event.location && (
                    <div className="flex items-start gap-3 text-lcars-tan">
                      <MapPin className="w-4 h-4 mt-0.5" />
                      <a 
                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cleanIcsString(event.location))}`} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="hover:underline tracking-widest"
                      >
                        {cleanIcsString(event.location)}
                      </a>
                    </div>
                  )}
                  {event.host && (
                    <div className="flex items-center gap-3 text-lcars-gold">
                      <User className="w-4 h-4" />
                      <span className="tracking-widest">HOST: {event.host}</span>
                    </div>
                  )}
                  <button 
                    onClick={() => setSelectedEventForDetails(event)}
                    className="lcars-pill bg-lcars-blue text-black w-full mt-4 flex items-center justify-between px-4 py-2 text-[0.7rem] font-bold tracking-widest"
                  >
                    <span>VIEW MISSION BRIEFING</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="p-6 pt-0 mt-auto space-y-2">
                {(() => {
                  const userAtt = attendance.find(a => a.eventId === event.id && a.userId === currentUser.uid);
                  if (userAtt?.status === 'attending') {
                    return (
                      <div className="w-full flex items-center justify-between py-1.5 px-3 bg-lcars-blue/15 rounded-full border border-lcars-blue text-lcars-blue font-bold text-[0.7rem] tracking-wider uppercase">
                        <span>✅ Attendance Confirmed (+{event.points} pts)</span>
                        <button
                          onClick={() => handleRSVP(event.id!, 'not-attending')}
                          className="text-[0.6rem] text-white/50 hover:text-lcars-red transition-colors ml-2 uppercase"
                        >
                          Remove
                        </button>
                      </div>
                    );
                  } else if (userAtt?.status === 'pending') {
                    return (
                      <div className="w-full flex items-center justify-between py-1.5 px-3 bg-lcars-gold/15 rounded-full border border-lcars-gold text-lcars-gold font-bold text-[0.7rem] tracking-wider uppercase">
                        <span className="truncate">⏳ Pending Verification (+{event.points} pts)</span>
                        <button
                          onClick={() => handleRSVP(event.id!, 'not-attending')}
                          className="text-[0.6rem] text-white/50 hover:text-lcars-red transition-colors ml-2 uppercase shrink-0"
                        >
                          Cancel
                        </button>
                      </div>
                    );
                  } else {
                    return (
                      <button
                        onClick={() => handleRSVP(event.id!, 'attending')}
                        className="w-full py-1.5 px-4 bg-lcars-gold text-black rounded-full hover:bg-white transition-colors font-bold text-[0.7rem] tracking-widest uppercase"
                      >
                        + Log My Attendance (+{event.points} pts)
                      </button>
                    );
                  }
                })()}
              </div>
            </div>
          );
        })}
        {filteredEvents.length === 0 && (
          <div className="col-span-full py-24 text-center bg-lcars-blue/5 border-2 border-dashed border-lcars-blue/20 rounded-lg">
            <CalendarIcon className="w-16 h-16 mx-auto mb-4 text-lcars-blue opacity-20" />
            <p className="font-bold tracking-[0.3em] text-lcars-blue opacity-40">NO MISSIONS FOUND FOR THIS PERIOD.</p>
          </div>
        )}
      </div>

      {/* Convention Details Modal */}
      <Dialog open={!!selectedEventForDetails} onOpenChange={() => setSelectedEventForDetails(null)}>
        <DialogContent className="bg-card-bg border border-border-color text-[var(--foreground)] max-w-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader className="border-b border-border-color pb-4">
            <DialogTitle className="uppercase text-accent-blue tracking-widest font-bold flex items-center gap-3">
              <Info className="w-5 h-5" />
              Mission Briefing: {cleanIcsString(selectedEventForDetails?.name)}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-6 space-y-8">
            {/* Attendance Status Banner in Dialog */}
            {selectedEventForDetails && (() => {
              const userAtt = attendance.find(a => a.eventId === selectedEventForDetails.id && a.userId === currentUser.uid);
              return (
                <div className="p-4 rounded-xl border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-black/40 border-white/10">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-lcars-gold">
                      Your Attendance Record
                    </div>
                    <p className="text-xs text-[var(--foreground)]/70 mt-0.5">
                      {userAtt?.status === 'attending' 
                        ? `Confirmed! You have been awarded ${selectedEventForDetails.points} points for this mission.`
                        : userAtt?.status === 'pending'
                        ? `Attendance is currently Pending. An Admin or Con Planner will verify your attendance to award your ${selectedEventForDetails.points} points.`
                        : `Were you in attendance for this event? Log your attendance to receive ${selectedEventForDetails.points} points.`}
                    </p>
                  </div>
                  <div>
                    {userAtt?.status === 'attending' ? (
                      <button
                        onClick={() => handleRSVP(selectedEventForDetails.id!, 'not-attending')}
                        className="lcars-pill bg-lcars-red text-[var(--foreground)] px-4 py-1 text-xs font-bold"
                      >
                        WITHDRAW ATTENDANCE
                      </button>
                    ) : userAtt?.status === 'pending' ? (
                      <button
                        onClick={() => handleRSVP(selectedEventForDetails.id!, 'not-attending')}
                        className="lcars-pill bg-lcars-tan text-black px-4 py-1 text-xs font-bold"
                      >
                        CANCEL REQUEST
                      </button>
                    ) : (
                      <button
                        onClick={() => handleRSVP(selectedEventForDetails.id!, 'attending')}
                        className="lcars-pill bg-lcars-gold text-black px-4 py-1 text-xs font-bold hover:bg-white transition-colors"
                      >
                        LOG MY ATTENDANCE
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Event Summary / After Action */}
            {selectedEventForDetails?.summary && (
              <div className="space-y-3">
                <h4 className="text-[0.7rem] font-bold text-lcars-blue uppercase tracking-widest border-l-4 border-lcars-blue pl-3">
                  Mission Summary & After-Action Report
                </h4>
                <p className="text-sm text-[var(--foreground)]/80 leading-relaxed whitespace-pre-wrap pl-4 bg-white/5 p-3 rounded border border-white/10">
                  {cleanIcsString(selectedEventForDetails.summary)}
                </p>
              </div>
            )}

            {/* Lessons Learned */}
            {selectedEventForDetails?.lessonsLearned && (
              <div className="space-y-3">
                <h4 className="text-[0.7rem] font-bold text-lcars-gold uppercase tracking-widest border-l-4 border-lcars-gold pl-3">
                  Tactical Lessons Learned
                </h4>
                <p className="text-sm text-[var(--foreground)]/80 leading-relaxed whitespace-pre-wrap pl-4 bg-white/5 p-3 rounded border border-white/10">
                  {cleanIcsString(selectedEventForDetails.lessonsLearned)}
                </p>
              </div>
            )}

            {selectedEventForDetails?.pertinentInfo && (
              <div className="space-y-3">
                <h4 className="text-[0.7rem] font-bold text-accent-gold uppercase tracking-widest border-l-4 border-accent-gold pl-3">Pertinent Information</h4>
                <p className="text-sm text-[var(--foreground)]/80 leading-relaxed whitespace-pre-wrap pl-4">
                  {cleanIcsString(selectedEventForDetails.pertinentInfo)}
                </p>
              </div>
            )}

            {selectedEventForDetails?.sessions && selectedEventForDetails.sessions.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-[0.7rem] font-bold text-accent-blue uppercase tracking-widest border-l-4 border-accent-blue pl-3">Designated Scenes</h4>
                <div className="grid grid-cols-1 gap-3 pl-4">
                  {selectedEventForDetails.sessions.map((session) => (
                    <div key={session.id} className="bg-white/5 p-4 rounded border border-border-color flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div>
                        <div className="font-bold text-[var(--foreground)]">{cleanIcsString(session.name)}</div>
                        {session.location && (
                          <div className="text-xs text-[var(--foreground)]/40 mt-1 uppercase tracking-wider">
                            <a 
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cleanIcsString(session.location))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-accent-blue transition-colors underline decoration-white/20 underline-offset-2"
                            >
                              {cleanIcsString(session.location)}
                            </a>
                          </div>
                        )}
                      </div>
                      <div className="bg-accent-blue/20 text-accent-blue px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap">
                        {session.startTime} - {session.endTime}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedEventForDetails?.schedule && selectedEventForDetails.schedule.length > 0 && (
              <div className="space-y-4">
                <h4 className="text-[0.7rem] font-bold text-accent-purple uppercase tracking-widest border-l-4 border-accent-purple pl-3">Mission Timeline (Schedule)</h4>
                <div className="space-y-3 pl-4">
                  {selectedEventForDetails.schedule.map((item) => (
                    <div key={item.id} className="flex gap-4 items-start">
                      <div className="w-24 shrink-0 font-bold text-accent-purple text-sm pt-0.5">{cleanIcsString(item.time)}</div>
                      <div className="flex-1 bg-white/5 p-3 rounded border border-border-color">
                        <div className="text-sm font-bold text-[var(--foreground)]">{cleanIcsString(item.activity)}</div>
                        {item.location && (
                          <div className="text-[0.65rem] text-[var(--foreground)]/40 mt-1 uppercase tracking-widest">
                            <a 
                              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(cleanIcsString(item.location))}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="hover:text-accent-purple transition-colors underline decoration-white/20 underline-offset-2"
                            >
                              {cleanIcsString(item.location)}
                            </a>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!selectedEventForDetails?.pertinentInfo && 
             (!selectedEventForDetails?.sessions || selectedEventForDetails.sessions.length === 0) && 
             (!selectedEventForDetails?.schedule || selectedEventForDetails.schedule.length === 0) && (
              <div className="text-center py-6 text-[var(--foreground)]/40 italic text-sm">
                NO ADDITIONAL MISSION DATA AVAILABLE FOR THIS EVENT.
              </div>
            )}

            {selectedEventForDetails && (
              <EventChat 
                eventId={selectedEventForDetails.id!} 
                eventName={cleanIcsString(selectedEventForDetails.name)}
                currentUserProfile={currentUser}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
